import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { ScanSearch, RefreshCw, ExternalLink, MapPin, Flame, BedDouble, Wallet, AlertTriangle, Sparkles } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SCAN_STEPS = [
    "Scansione gruppi Facebook…",
    "Analisi post Instagram…",
    "Estrazione budget, zona e locali con l'IA…",
    "Calcolo Match Score con i tuoi annunci…",
];
const MIN_RADAR_MS = 1800;
const DATE_FILTERS = [
    { value: "24h", label: "Ultime 24 ore (Hot)" },
    { value: "48h", label: "Ultime 48 ore" },
    { value: "week", label: "Ultima settimana" },
    { value: "month", label: "Ultimo mese" },
];
const eur = (n) => `€ ${Number(n).toLocaleString("it-IT")}`;
const fmtWhen = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return null; }
};

export default function SocialBuyerRadar({ limit, onSeeAll }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [scanning, setScanning] = useState(true);
    const [scanMsg, setScanMsg] = useState("");
    const [step, setStep] = useState(0);
    const [dateFilter, setDateFilter] = useState("24h");
    const autoScanned = useRef(new Set());

    const load = useCallback(async (filter = dateFilter) => {
        const r = await api.get("/crm/social-radar", { params: { date_filter: filter } });
        setData(r.data);
        return r.data;
    }, [dateFilter]);

    const runScan = useCallback(async (filter = dateFilter) => {
        setScanning(true); setScanMsg("");
        const started = Date.now();
        try {
            const r = await api.post("/crm/social-radar/refresh", {}, { params: { date_filter: filter } });
            const n = r.data.synced_cities?.length || 0;
            setScanMsg(r.data.grounding_unavailable
                ? "Scansione momentaneamente non disponibile (crediti di ricerca esauriti). Riprova più tardi."
                : n > 0 ? `Scansione completata su ${n} città` : "Scansione già eseguita di recente: dati aggiornati.");
            await load(filter);
        } catch (e) {
            setScanMsg(e?.response?.data?.detail || "Errore durante la scansione");
        } finally {
            setTimeout(() => setScanning(false), Math.max(0, MIN_RADAR_MS - (Date.now() - started)));
        }
    }, [dateFilter, load]);

    useEffect(() => {
        let alive = true;
        const started = Date.now();
        setScanning(true);
        setError(null);
        (async () => {
            try {
                const d = await load(dateFilter);
                if (alive && d.stale && d.cities?.length && !autoScanned.current.has(dateFilter)) {
                    autoScanned.current.add(dateFilter);
                    await runScan(dateFilter);
                    return;
                }
            } catch (e) {
                if (alive) setError({ status: e?.response?.status, detail: e?.response?.data?.detail });
            }
            setTimeout(() => { if (alive) setScanning(false); }, Math.max(0, MIN_RADAR_MS - (Date.now() - started)));
        })();
        return () => { alive = false; };
    }, [dateFilter, load, runScan]);

    useEffect(() => {
        if (!scanning) return undefined;
        const t = setInterval(() => setStep((s) => (s + 1) % SCAN_STEPS.length), 900);
        return () => clearInterval(t);
    }, [scanning]);

    useEffect(() => {
        let alive = true;
        const reloadProfileZones = async () => {
            autoScanned.current.clear();
            setScanMsg("");
            setData((current) => current ? {
                ...current,
                cities: [],
                items: [],
                count: 0,
                matched_count: 0,
                sync_meta: [],
                last_scan_at: null,
            } : current);
            setScanning(true);
            try {
                await load(dateFilter);
                if (alive) setError(null);
            } catch (e) {
                if (alive) setError({ status: e?.response?.status, detail: e?.response?.data?.detail });
            } finally {
                if (alive) setScanning(false);
            }
        };
        window.addEventListener("vendidirect:zone-operative-updated", reloadProfileZones);
        window.addEventListener("focus", reloadProfileZones);
        return () => {
            alive = false;
            window.removeEventListener("vendidirect:zone-operative-updated", reloadProfileZones);
            window.removeEventListener("focus", reloadProfileZones);
        };
    }, [dateFilter, load]);

    if (error?.status === 403) return null;

    const items = data?.items || [];
    const shown = limit ? items.slice(0, limit) : items;
    const cities = data?.cities || [];

    return (
        <section className="sbr-widget" data-testid="social-buyer-radar">
            <header className="sbr-head">
                <div className="sbr-head-left">
                    <div className="sbr-icon"><ScanSearch size={22} /></div>
                    <div>
                        <span className="sbr-eyebrow">
                            <Sparkles size={11} /> Radar Buyer Social · IA
                        </span>
                        <h2 className="sbr-title">Clienti Pronti sui Social</h2>
                        <p className="sbr-sub">
                            Tutti i potenziali acquirenti e affittuari delle tue zone operative, trovati su Facebook e Instagram: la tua arma di acquisizione.
                        </p>
                    </div>
                </div>
                <div className="sbr-head-right">
                    <div className="sbr-date-filter">
                        <span className="sbr-filter-label">Data post</span>
                        <Select value={dateFilter} onValueChange={setDateFilter} disabled={scanning}>
                            <SelectTrigger data-testid="sbr-date-filter" className="sbr-filter-trigger" aria-label="Filtra per data del post">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {DATE_FILTERS.map((filter) => (
                                    <SelectItem key={filter.value} value={filter.value} data-testid={`sbr-date-filter-${filter.value}`}>
                                        {filter.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {cities.length > 0 && (
                        <div className="sbr-cities" data-testid="sbr-cities">
                            {cities.map((c) => <span key={c} className="sbr-city-chip"><MapPin size={10} /> {c}</span>)}
                        </div>
                    )}
                    <button
                        type="button"
                        data-testid="sbr-scan-btn"
                        onClick={() => runScan(dateFilter)}
                        disabled={scanning || !cities.length || !!error}
                        className="sbr-scan-btn"
                    >
                        <RefreshCw size={13} className={scanning ? "animate-spin" : ""} /> {scanning ? "Scansione…" : "Scansiona ora"}
                    </button>
                </div>
            </header>

            {error && error.status !== 403 && (
                <div className="sbr-note sbr-note-warn" data-testid="sbr-error-note">
                    <AlertTriangle size={13} />
                    <span>{error.detail || "Radar momentaneamente non disponibile. Riprova tra qualche istante."}</span>
                </div>
            )}

            {!error && scanning && <RadarScan cities={cities} step={step} />}

            {!error && !scanning && (
                <>
                    {(data?.grounding_unavailable || scanMsg) && (
                        <div className={`sbr-note ${data?.grounding_unavailable ? "sbr-note-warn" : ""}`} data-testid="sbr-status-note">
                            {data?.grounding_unavailable && <AlertTriangle size={13} />}
                            <span>
                                {data?.grounding_unavailable
                                    ? "Scansione social momentaneamente non disponibile (crediti di ricerca esauriti). I risultati si aggiorneranno automaticamente appena il servizio torna operativo."
                                    : scanMsg}
                            </span>
                        </div>
                    )}

                    {!cities.length ? (
                        <div className="sbr-empty" data-testid="sbr-empty-cities">
                            Configura almeno una Zona Operativa nel profilo agenzia per attivare la scansione social.
                        </div>
                    ) : shown.length === 0 ? (
                        <div className="sbr-empty" data-testid="sbr-empty">
                            Nessun post di ricerca casa trovato nelle tue zone al momento. Il radar continua a scansionare ogni giorno.
                        </div>
                    ) : (
                        <div className="sbr-grid" data-testid="sbr-grid">
                            {shown.map((it, i) => <HotLeadCard key={it.id} item={it} index={i} />)}
                        </div>
                    )}

                    <footer className="sbr-foot">
                        <span data-testid="sbr-stats">
                            {items.length} hot lead{items.length === 1 ? "" : "s"}
                            {data?.matched_count != null && <> · {data.matched_count} con match</>}
                            {data?.last_scan_at && <> · ultima scansione {fmtWhen(data.last_scan_at)}</>}
                        </span>
                        <span className="sbr-foot-right">
                            {limit && items.length > limit && onSeeAll && (
                                <button type="button" data-testid="sbr-see-all" onClick={onSeeAll} className="sbr-link">Vedi tutti ({items.length}) →</button>
                            )}
                        </span>
                    </footer>
                </>
            )}
        </section>
    );
}

function RadarScan({ cities, step }) {
    return (
        <div className="sbr-scan" data-testid="sbr-scanning">
            <div className="sbr-radar" aria-hidden="true">
                <span className="sbr-radar-ring sbr-r1" /><span className="sbr-radar-ring sbr-r2" /><span className="sbr-radar-ring sbr-r3" />
                <span className="sbr-radar-cross" /><span className="sbr-radar-cross sbr-cross-v" />
                <span className="sbr-radar-sweep" />
                <span className="sbr-blip sbr-b1" /><span className="sbr-blip sbr-b2" /><span className="sbr-blip sbr-b3" />
                <span className="sbr-radar-core" />
            </div>
            <div className="sbr-scan-text">
                <div className="sbr-scan-title">L'IA sta scansionando i social in tempo reale</div>
                <div className="sbr-scan-step" key={step}>{SCAN_STEPS[step]}</div>
                {cities.length > 0 && <div className="sbr-scan-cities">{cities.join(" · ")}</div>}
            </div>
        </div>
    );
}

function HotLeadCard({ item, index }) {
    const score = item.match_score;
    const tone = score >= 75 ? "hot" : score >= 55 ? "warm" : score ? "mild" : "none";
    const isRent = item.transaction_type === "affitto";
    return (
        <article className={`sbr-card sbr-tone-${tone}`} style={{ animationDelay: `${index * 70}ms` }} data-testid={`sbr-card-${item.id}`}>
            <div className="sbr-card-head">
                <span className={`sbr-source sbr-source-${item.source}`} data-testid={`sbr-source-${item.id}`}>
                    {item.source === "instagram" ? <InstagramGlyph /> : <FacebookGlyph />}
                    {item.source === "instagram" ? "Instagram" : "Facebook"}
                </span>
                <span className="sbr-city"><MapPin size={11} /> {item.city}{item.zone_hint ? ` · ${item.zone_hint}` : ""}</span>
                <span className="sbr-card-signals">
                    {item.is_new && <span className="sbr-new" data-testid={`sbr-new-${item.id}`}><Flame size={11} /> NEW</span>}
                    {item.urgency === "alta" && <span className="sbr-urgent"><Flame size={11} /> Urgente</span>}
                </span>
            </div>

            <div className="sbr-label">Cosa cercano</div>
            <p className="sbr-summary" data-testid={`sbr-summary-${item.id}`}>{item.summary}</p>
            <div className="sbr-chips">
                {item.transaction_type && <span className="sbr-chip sbr-chip-accent">{isRent ? "Affitto" : "Acquisto"}</span>}
                {item.budget_max && <span className="sbr-chip"><Wallet size={10} /> fino a {eur(item.budget_max)}{isRent ? "/mese" : ""}</span>}
                {item.min_rooms && <span className="sbr-chip"><BedDouble size={10} /> {item.min_rooms}+ locali</span>}
                {(item.property_types || []).slice(0, 2).map((t) => <span key={t} className="sbr-chip sbr-chip-muted">{t.replace("_", " ")}</span>)}
                {(item.features || []).slice(0, 2).map((f) => <span key={f} className="sbr-chip sbr-chip-muted">{f}</span>)}
            </div>

            <div className="sbr-match">
                <ScoreRing score={score} tone={tone} />
                <div className="sbr-match-body">
                    <div className="sbr-label">Match Score</div>
                    {item.best_match ? (
                        <>
                            <div className="sbr-match-title" data-testid={`sbr-match-title-${item.id}`}>{item.best_match.title}</div>
                            <div className="sbr-match-sub">{item.best_match.city} · {eur(item.best_match.price)}{item.matches?.length > 1 && <> · +{item.matches.length - 1} altr{item.matches.length - 1 === 1 ? "o" : "i"}</>}</div>
                        </>
                    ) : (
                        <div className="sbr-match-sub">Nessun tuo annuncio compatibile — <b>opportunità di acquisizione</b></div>
                    )}
                </div>
            </div>

            <a href={item.post_url} target="_blank" rel="noreferrer noopener" className="sbr-cta" data-testid={`sbr-goto-${item.id}`}>
                <ExternalLink size={13} /> Vai al Post
            </a>
        </article>
    );
}

function ScoreRing({ score, tone }) {
    const r = 24;
    const c = 2 * Math.PI * r;
    const pct = Math.min(100, Math.max(0, score || 0));
    return (
        <div className={`sbr-ring sbr-ring-${tone}`} data-testid="sbr-score-ring">
            <svg viewBox="0 0 60 60">
                <circle className="sbr-ring-bg" cx="30" cy="30" r={r} />
                <circle className="sbr-ring-fg" cx="30" cy="30" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
            </svg>
            <span>{score ? `${score}%` : "—"}</span>
        </div>
    );
}

function FacebookGlyph() {
    return <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z" /></svg>;
}

function InstagramGlyph() {
    return <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.7.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.2-4.35-2.62-6.78-6.98-6.98C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z" /></svg>;
}
